const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

class ApiClient {
  private token: string | null = null;
  private refreshToken: string | null = null;
  private refreshPromise: Promise<boolean> | null = null;

  constructor() {
    this.token = localStorage.getItem('admin_token');
    this.refreshToken = localStorage.getItem('admin_refresh_token');
  }

  setToken(token: string) {
    this.token = token;
    localStorage.setItem('admin_token', token);
  }

  setRefreshToken(token: string) {
    this.refreshToken = token;
    localStorage.setItem('admin_refresh_token', token);
  }

  clearToken() {
    localStorage.removeItem('platform_tenant_id');   // өөр эрхээр нэвтрэхэд хуучин СӨХ сонголт үлдэхгүй
    this.token = null;
    this.refreshToken = null;
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_refresh_token');
  }

  get baseUrl() {
    return BASE_URL;
  }

  getToken(): string | null {
    return this.token;
  }

  isAuthenticated(): boolean {
    return this.token !== null;
  }

  private headers(): Record<string, string> {
    const h: Record<string, string> = { 'Content-Type': 'application/json' };
    if (this.token) h['Authorization'] = `Bearer ${this.token}`;
    const pt = localStorage.getItem('platform_tenant_id'); if (pt) h['X-Tenant-Id'] = pt;   // платформ админ СӨХ сонгосон
    return h;
  }

  private async tryRefresh(): Promise<boolean> {
    if (!this.refreshToken) return false;
    if (this.refreshPromise) return this.refreshPromise;
    this.refreshPromise = (async () => {
      try {
        const res = await fetch(`${BASE_URL}/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: this.refreshToken }),
        });
        if (!res.ok) return false;
        const data = await res.json();
        this.setToken(data.access_token);
        if (data.refresh_token) this.setRefreshToken(data.refresh_token);
        return true;
      } catch {
        return false;
      } finally {
        this.refreshPromise = null;
      }
    })();
    return this.refreshPromise;
  }

  private async request<T>(method: string, path: string, body?: any): Promise<T> {
    let res = await fetch(`${BASE_URL}${path}`, {
      method,
      headers: this.headers(),
      body: body ? JSON.stringify(body) : undefined,
    });
    if (res.status === 401 && !path.includes('/auth/')) {
      const refreshed = await this.tryRefresh();
      if (refreshed) {
        res = await fetch(`${BASE_URL}${path}`, {
          method,
          headers: this.headers(),
          body: body ? JSON.stringify(body) : undefined,
        });
      } else {
        this.clearToken();
        localStorage.removeItem('admin_user');
        window.location.href = '/login';
        throw new ApiError(401, 'Session expired');
      }
    }
    if (!res.ok) throw new ApiError(res.status, await res.text());
    return res.json();
  }

  async get<T = any>(path: string): Promise<T> {
    return this.request('GET', path);
  }

  async post<T = any>(path: string, body?: any): Promise<T> {
    return this.request('POST', path, body);
  }

  async put<T = any>(path: string, body?: any): Promise<T> {
    return this.request('PUT', path, body);
  }

  async patch<T = any>(path: string, body?: any): Promise<T> {
    return this.request('PATCH', path, body);
  }

  async delete<T = any>(path: string): Promise<T> {
    return this.request('DELETE', path);
  }
}

class ApiError extends Error {
  constructor(public status: number, public body: string) {
    super(`API Error ${status}: ${body}`);
  }
}

export const api = new ApiClient();
export { ApiError };
